import { computed, inject, Injectable, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import {
  AssignTicketDto,
  AttachmentResponseDtoApiResponse,
  ChangeStatusDto,
  CommentResponseDtoApiResponse,
  CreateCommentDto,
  CreateTicketDto,
  SlaPolicyResponseDto,
  SubmitCsatDto,
  SupportMetricsDtoApiResponse,
  SupportTagResponseDto,
  SupportTicketsApiService,
  TicketPriority,
  TicketResponseDtoApiResponse,
  TicketStatus,
  TicketSummaryDtoPagedResultApiResponse,
  TicketType,
  UpdateTicketDto,
  VoteResponseDtoApiResponse,
} from 'api';

import { AuthService } from '@auth/services/auth.service';
import { MetricColor } from '@shared/components/metric-card/metric-card.component';
import { SEVERITY_TAG, SeverityTag } from '@shared/constants/severity-tag';
import { environment } from '@/environments/environment';
import { readResource } from '@core/utils';

/**
 * Forma del feed de actividad de tickets. El endpoint `GET /support-tickets/activity`
 * dejó de exponer un tipo en swagger (responde sin esquema), por lo que se tipa localmente.
 */
export interface TicketActivityItem {
  id?: string;
  activityType?: string;
  ticketNumber?: string;
  activitySummary?: string;
  lastActivityAt?: string;
}

export interface TicketActivityResponse {
  // `since`: ventana realmente aplicada por el backend (recortada a 30 días si se envía una anterior).
  // Es autoritativa; el cliente debería persistir ese valor. Ver widget de soporte.
  data?: { items?: TicketActivityItem[]; since?: string };
}

@Injectable({ providedIn: 'root' })
export class SupportTicketsService {
  private readonly api = inject(SupportTicketsApiService);
  private readonly authService = inject(AuthService);

  private readonly tenantId = computed(() => this.authService.user()?.tenantId ?? null);

  // =============================================================================
  // LISTA — FILTROS Y PAGINACIÓN
  // =============================================================================
  private readonly _search = signal<string | null>(null);
  private readonly _statusFilter = signal<TicketStatus | null>(null);
  private readonly _typeFilter = signal<TicketType | null>(null);
  private readonly _priorityFilter = signal<TicketPriority | null>(null);
  private readonly _slaBreachedFilter = signal<boolean | null>(null);
  private readonly _assignedToIdFilter = signal<string | null>(null);
  private readonly _page = signal<number>(1);
  private readonly _pageSize = signal<number>(20);

  private readonly ticketsRs = rxResource({
    params: () => ({
      tenantId: this.tenantId(),
      page: this._page(),
      pageSize: this._pageSize(),
      search: this._search(),
      status: this._statusFilter(),
      type: this._typeFilter(),
      priority: this._priorityFilter(),
      slaBreached: this._slaBreachedFilter(),
      assignedToId: this._assignedToIdFilter(),
    }),
    stream: ({ params }) => {
      if (!params.tenantId) return of(null);
      return this.api.apiV1SupportTicketsGet({
        tenantId: params.tenantId,
        page: params.page,
        pageSize: params.pageSize,
        search: params.search ?? undefined,
        status: params.status ?? undefined,
        type: params.type ?? undefined,
        priority: params.priority ?? undefined,
        slaBreached: params.slaBreached ?? undefined,
        assignedToId: params.assignedToId ?? undefined,
      });
    },
  });

  public readonly statusFilter = this._statusFilter.asReadonly();
  public readonly typeFilter = this._typeFilter.asReadonly();
  public readonly priorityFilter = this._priorityFilter.asReadonly();
  public readonly slaBreachedFilter = this._slaBreachedFilter.asReadonly();
  public readonly assignedToIdFilter = this._assignedToIdFilter.asReadonly();
  public readonly tickets = readResource(this.ticketsRs, (v) => v?.data?.items ?? [], []);
  public readonly totalCount = readResource(this.ticketsRs, (v) => v?.data?.totalCount ?? 0, 0);
  public readonly currentPage = readResource(this.ticketsRs, (v) => v?.data?.page ?? this._page(), this._page());
  public readonly pageSize = readResource(
    this.ticketsRs,
    (v) => v?.data?.pageSize ?? this._pageSize(),
    this._pageSize()
  );
  public readonly isLoading = computed(() => this.ticketsRs.isLoading());
  public readonly reload = () => this.ticketsRs.reload();

  // =============================================================================
  // DETALLE — POR ID
  // =============================================================================
  private readonly _ticketId = signal<string | null>(null);
  private readonly _ticketTenantIdOverride = signal<string | null>(null);

  private readonly ticketRs = rxResource({
    params: () => ({
      id: this._ticketId(),
      tenantId: this._ticketTenantIdOverride() ?? this.tenantId(),
    }),
    stream: ({ params }) => {
      if (!params.id || !params.tenantId) return of(null);
      return this.api.apiV1SupportTicketsIdGet({ id: params.id, tenantId: params.tenantId });
    },
  });

  // ⚠️ Leer `value()` de un rxResource en estado `error` RELANZA el error. Como este
  // `ticket` se consume desde computeds que el template evalúa SIEMPRE (p. ej. el toolbar
  // del título: `toolbarTags()`/`toolbarActions()`), un 404/5xx haría que la excepción
  // escape a change detection → ErrorHandler global → Rollbar (ruido). La guarda
  // `hasValue()` devuelve `null` en error/carga y deja que la UI muestre `ticketError()`.
  public readonly ticket = computed(() => (this.ticketRs.hasValue() ? (this.ticketRs.value()?.data ?? null) : null));
  public readonly isLoadingTicket = computed(() => this.ticketRs.isLoading());
  public readonly ticketError = computed(() => this.ticketRs.error());
  public readonly reloadTicket = () => this.ticketRs.reload();

  public loadTicket(id: string, tenantIdOverride?: string): void {
    this._ticketId.set(id);
    this._ticketTenantIdOverride.set(tenantIdOverride ?? null);
  }

  // =============================================================================
  // FILTROS — MÉTODOS
  // =============================================================================
  public setFilters(filters: {
    search?: string | null;
    status?: TicketStatus | null;
    type?: TicketType | null;
    priority?: TicketPriority | null;
    slaBreached?: boolean | null;
    assignedToId?: string | null;
  }): void {
    if (filters.search !== undefined) this._search.set(filters.search);
    if (filters.status !== undefined) this._statusFilter.set(filters.status);
    if (filters.type !== undefined) this._typeFilter.set(filters.type);
    if (filters.priority !== undefined) this._priorityFilter.set(filters.priority);
    if (filters.slaBreached !== undefined) this._slaBreachedFilter.set(filters.slaBreached);
    if (filters.assignedToId !== undefined) this._assignedToIdFilter.set(filters.assignedToId);
    this._page.set(1);
  }

  public clearFilters(): void {
    this._search.set(null);
    this._statusFilter.set(null);
    this._typeFilter.set(null);
    this._priorityFilter.set(null);
    this._slaBreachedFilter.set(null);
    this._assignedToIdFilter.set(null);
    this._page.set(1);
  }

  public setPage(page: number): void {
    this._page.set(Math.max(1, page));
  }

  // =============================================================================
  // CRUD
  // =============================================================================
  public create(dto: CreateTicketDto): Observable<TicketResponseDtoApiResponse> {
    return this.api.apiV1SupportTicketsPost({ createTicketDto: dto }).pipe(tap(() => this.reload()));
  }

  public update(id: string, dto: UpdateTicketDto): Observable<TicketResponseDtoApiResponse> {
    const tenantId = this._ticketTenantIdOverride() ?? this.tenantId();
    if (!tenantId) return throwError(() => new Error('tenantId es requerido'));
    return this.api
      .apiV1SupportTicketsIdPut({ id, tenantId, updateTicketDto: dto })
      .pipe(tap(() => this.reloadTicket()));
  }

  public changeStatus(id: string, dto: ChangeStatusDto): Observable<TicketResponseDtoApiResponse> {
    const tenantId = this._ticketTenantIdOverride() ?? this.tenantId();
    if (!tenantId) return throwError(() => new Error('tenantId es requerido'));
    return this.api.apiV1SupportTicketsIdStatusPatch({ id, tenantId, changeStatusDto: dto }).pipe(
      tap(() => {
        this.reloadTicket();
        this.reload();
      })
    );
  }

  public assign(id: string, dto: AssignTicketDto): Observable<TicketResponseDtoApiResponse> {
    const tenantId = this._ticketTenantIdOverride() ?? this.tenantId();
    if (!tenantId) return throwError(() => new Error('tenantId es requerido'));
    return this.api
      .apiV1SupportTicketsIdAssignPatch({ id, tenantId, assignTicketDto: dto })
      .pipe(tap(() => this.reloadTicket()));
  }

  public addComment(id: string, dto: CreateCommentDto): Observable<CommentResponseDtoApiResponse> {
    const tenantId = this._ticketTenantIdOverride() ?? this.tenantId();
    if (!tenantId) return throwError(() => new Error('tenantId es requerido'));
    return this.api
      .apiV1SupportTicketsIdCommentsPost({ id, tenantId, createCommentDto: dto })
      .pipe(tap(() => this.reloadTicket()));
  }

  public uploadAttachment(id: string, file: File): Observable<AttachmentResponseDtoApiResponse> {
    const tenantId = this._ticketTenantIdOverride() ?? this.tenantId();
    if (!tenantId) return throwError(() => new Error('tenantId es requerido'));
    return this.api.apiV1SupportTicketsIdAttachmentsPost({ id, tenantId, file });
  }

  public toggleVote(id: string): Observable<VoteResponseDtoApiResponse> {
    const tenantId = this._ticketTenantIdOverride() ?? this.tenantId();
    if (!tenantId) return throwError(() => new Error('tenantId es requerido'));
    return this.api.apiV1SupportTicketsIdVotePost({ id, tenantId }).pipe(tap(() => this.reloadTicket()));
  }

  public submitCsat(id: string, dto: SubmitCsatDto): Observable<TicketResponseDtoApiResponse> {
    const tenantId = this._ticketTenantIdOverride() ?? this.tenantId();
    if (!tenantId) return throwError(() => new Error('tenantId es requerido'));
    return this.api
      .apiV1SupportTicketsIdCsatPatch({ id, tenantId, submitCsatDto: dto })
      .pipe(tap(() => this.reloadTicket()));
  }

  public getMetrics(tenantId: string, from?: string, to?: string): Observable<SupportMetricsDtoApiResponse> {
    return this.api.apiV1SupportTicketsMetricsGet({ tenantId, from, to });
  }

  /**
   * Búsqueda de tickets con parámetros explícitos (paginado y filtros opcionales).
   * Pensada para el **panel admin**, que lista tickets de un tenant **arbitrario** (o de todos):
   * ese caso no lo cubre la señal reactiva `tickets`, atada al tenant autenticado y a sus filtros.
   * Consulta independiente: no altera el listado principal.
   */
  public searchTickets(params: {
    tenantId?: string;
    status?: TicketStatus;
    priority?: TicketPriority;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Observable<TicketSummaryDtoPagedResultApiResponse> {
    return this.api.apiV1SupportTicketsGet({
      tenantId: params.tenantId,
      status: params.status,
      priority: params.priority,
      search: params.search,
      page: params.page ?? 1,
      pageSize: params.pageSize,
    });
  }

  /**
   * Roadmap público de mejoras/sugerencias (paginado, con filtro opcional por tipo).
   * Consulta independiente: no comparte estado con el listado principal (`tickets`).
   */
  public getRoadmap(params: {
    tenantId: string;
    type?: TicketType;
    page?: number;
    pageSize?: number;
  }): Observable<TicketSummaryDtoPagedResultApiResponse> {
    return this.api.apiV1SupportTicketsRoadmapGet({
      tenantId: params.tenantId,
      type: params.type,
      page: params.page ?? 1,
      pageSize: params.pageSize,
    });
  }

  public getActivity(tenantId: string, since: Date): Observable<TicketActivityResponse> {
    return this.api.apiV1SupportTicketsActivityGet({ tenantId, since: since.toISOString() });
  }

  // =============================================================================
  // HELPERS UI
  // =============================================================================
  public getStatusInfo(status?: TicketStatus | null): { label: string; severity: SeverityTag } {
    switch (status) {
      case TicketStatus.Open:
        return { label: 'Abierto', severity: SEVERITY_TAG.info };
      case TicketStatus.InProgress:
        return { label: 'En progreso', severity: SEVERITY_TAG.warn };
      case TicketStatus.WaitingCustomer:
        return { label: 'Esperando cliente', severity: SEVERITY_TAG.secondary };
      case TicketStatus.Resolved:
        return { label: 'Resuelto', severity: SEVERITY_TAG.success };
      case TicketStatus.Closed:
        return { label: 'Cerrado', severity: SEVERITY_TAG.contrast };
      default:
        return { label: 'Desconocido', severity: SEVERITY_TAG.secondary };
    }
  }

  public getPriorityInfo(priority?: TicketPriority | null): {
    label: string;
    severity: SeverityTag;
    color: MetricColor;
  } {
    switch (priority) {
      case TicketPriority.Critical:
        return { label: 'Crítica', severity: SEVERITY_TAG.danger, color: 'red' };
      case TicketPriority.High:
        return { label: 'Alta', severity: SEVERITY_TAG.warn, color: 'orange' };
      case TicketPriority.Medium:
        return { label: 'Media', severity: SEVERITY_TAG.info, color: 'blue' };
      case TicketPriority.Low:
        return { label: 'Baja', severity: SEVERITY_TAG.secondary, color: 'teal' };
      default:
        return { label: 'Sin prioridad', severity: SEVERITY_TAG.secondary, color: 'teal' };
    }
  }

  public getTypeInfo(type?: TicketType | null): { label: string; icon: string } {
    switch (type) {
      case TicketType.Bug:
        return { label: 'Bug', icon: 'pi pi-bug' };
      case TicketType.Improvement:
        return { label: 'Mejora', icon: 'pi pi-arrow-up-right' };
      case TicketType.Suggestion:
        return { label: 'Sugerencia', icon: 'pi pi-lightbulb' };
      case TicketType.Support:
        return { label: 'Soporte', icon: 'pi pi-headphones' };
      default:
        return { label: 'Desconocido', icon: 'pi pi-question' };
    }
  }

  public getTagColor(_tag: SupportTagResponseDto): string {
    return 'secondary';
  }

  public getSlaInfo(policy: SlaPolicyResponseDto): string {
    return `1ra resp: ${policy.firstResponseHours}h · Resolución: ${policy.resolutionHours}h`;
  }

  // =============================================================================
  // IMÁGENES EN LÍNEA (Quill) — subida como adjuntos (OPCIÓN A)
  // =============================================================================
  // ⚠️ ESTADO ACTUAL (OPCIÓN D): este flujo NO se usa. Las imágenes de los
  // mensajes/comentarios quedan embebidas como base64 (optimizadas en el cliente,
  // ver utils/image-optimizer.ts) dentro del HTML. Motivo: un `<img src>` hacia
  // el endpoint de descarga no lleva los headers Authorization/X-Tenant-Domain
  // que añade el auth.interceptor → la imagen daba 404/rota.
  //
  // `processInlineImages` + `buildAttachmentUrl` se conservan como punto de
  // entrada para migrar a la OPCIÓN A si la opción D da problemas a futuro (HTML
  // muy pesado en BD, muchos adjuntos, imágenes grandes). Para reactivar la
  // opción A se requiere además que el backend sirva el adjunto por una URL
  // firmada/temporal cargable desde un `<img>` sin headers de auth.
  /**
   * Detecta imágenes embebidas como `data:` (base64) en el HTML de un editor Quill,
   * las sube como adjuntos del ticket y reescribe el `src` a la URL de descarga.
   * Si no hay imágenes base64, devuelve el HTML sin cambios. Requiere que el ticket
   * ya exista (se necesita su id para adjuntar).
   */
  public processInlineImages(ticketId: string, html: string): Observable<string> {
    const dataUrls = this.extractDataUrls(html);
    if (!dataUrls.length) return of(html);

    const uploads = dataUrls.map((dataUrl, i) =>
      this.uploadAttachment(ticketId, this.dataUrlToFile(dataUrl, `imagen-${Date.now()}-${i}`))
    );

    return forkJoin(uploads).pipe(
      map((results) => {
        let out = html;
        results.forEach((res, i) => {
          const attId = res.data?.id;
          if (attId) out = out.replace(dataUrls[i], this.buildAttachmentUrl(ticketId, attId));
        });
        return out;
      })
    );
  }

  private extractDataUrls(html: string): string[] {
    const regex = /<img[^>]+src="(data:[^"]+)"/g;
    const urls: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = regex.exec(html)) !== null) urls.push(match[1]);
    return urls;
  }

  private dataUrlToFile(dataUrl: string, baseName: string): File {
    const [meta, b64] = dataUrl.split(',');
    const mime = meta.match(/:(.*?);/)?.[1] ?? 'image/png';
    const ext = mime.split('/')[1] ?? 'png';
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], `${baseName}.${ext}`, { type: mime });
  }

  private buildAttachmentUrl(ticketId: string, attachmentId: string): string {
    const baseUrl = environment.backendBaseUrl;
    return `${baseUrl}/api/v1/support-tickets/${ticketId}/attachments/${attachmentId}/download`;
  }
}
